/* ──────────────────────────────────────────────────────────────────────
 * ForgotPasswordPage — public (unauthenticated) request-a-reset surface.
 *
 * Mounted at `/forgot-password` BEFORE the LoginProvider auth gate in
 * App.tsx so users without a session can reach it. Submits the operator's
 * email or phone to /api/auth/forgot-password and shows the same generic
 * success copy regardless of whether the identifier matched a real
 * account — the backend never confirms account existence, and neither
 * does this page.
 *
 * Visual language matches Login.tsx: navy → gold accent on a glass card
 * over the dark background image.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useMemo, useState } from "react";
import {
  Box, Button, Card, CircularProgress, TextField,
  ToggleButton, ToggleButtonGroup, Typography, Alert,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Link as RouterLink } from "react-router-dom";
import LoginImg from "../../Images/background.png";
import Logo from "../../Images/logo_main_black.webp";
import { requestPasswordReset } from "../../Api/rbacApi";

const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";

const ForgotPasswordPage: React.FC = () => {
  // Breakpoint switching is handled by MUI's sx-prop responsive arrays
  // ({xs, sm, md}); no useMediaQuery dependency needed here.
  const [method, setMethod] = useState<"email" | "sms">("email");
  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(() => identifier.trim().length > 0 && !submitting, [identifier, submitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const { message } = await requestPasswordReset(identifier.trim(), method);
      setSentMessage(message);
    } catch (err: any) {
      setError(err?.message || "Unable to send reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <Box sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", md: "flex-end" },
        px: { xs: 2, sm: 3, md: 0 },
        pr: { md: 20 },
      }}>
        <Box component="img" src={LoginImg} alt="Background"
          sx={{ objectFit: "cover", position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0 }} />
        <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 0 }} />

        <Box sx={{
          position: "relative", zIndex: 1,
          width: "100%",
          maxWidth: { xs: 420, sm: 440, md: 460 },
        }}>
            <Card sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 2,
              boxShadow: "0 18px 48px rgba(0,0,0,0.35)",
              bgcolor: "#fff",
              boxSizing: "border-box",
              width: "100%",
            }}>
              <Box sx={{ display: "flex", justifyContent: "center", pb: 3 }}>
                <img src={Logo} alt="Durham Convention Centre" style={{ maxWidth: 100, height: "auto" }} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY, textAlign: "center" }}>
                Forgot your password?
              </Typography>
              <Typography variant="body2" sx={{ color: "#475569", textAlign: "center", mt: 0.5, mb: 2.5 }}>
                Enter the email or phone on file and we'll send you a reset link.
              </Typography>

              {sentMessage && (
                <Alert severity="success" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: 13 } }}>
                  {sentMessage}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: 13 } }}>
                  {error}
                </Alert>
              )}

              {!sentMessage && (
                <form onSubmit={handleSubmit}>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={method}
                    onChange={(_, v) => v && setMethod(v)}
                    sx={{ mb: 2 }}
                    size="small"
                  >
                    <ToggleButton value="email" sx={{ textTransform: "none", fontWeight: 600,
                      "&.Mui-selected": { bgcolor: NAVY, color: "#fff", "&:hover": { bgcolor: "#1e3a5f" } } }}>
                      <EmailRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} /> Email
                    </ToggleButton>
                    <ToggleButton value="sms" sx={{ textTransform: "none", fontWeight: 600,
                      "&.Mui-selected": { bgcolor: NAVY, color: "#fff", "&:hover": { bgcolor: "#1e3a5f" } } }}>
                      <PhoneIphoneRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} /> SMS
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <TextField
                    fullWidth
                    autoFocus
                    label={method === "email" ? "Email address" : "Phone number"}
                    placeholder={method === "email" ? "you@example.com" : "(647) 555-1234"}
                    type={method === "email" ? "email" : "tel"}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete={method === "email" ? "email" : "tel"}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!canSubmit}
                    sx={{
                      bgcolor: NAVY, color: "#fff", py: 1.2,
                      textTransform: "none", fontWeight: 700,
                      "&:hover": { bgcolor: "#1e3a5f" },
                      "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" },
                    }}
                  >
                    {submitting && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
                    Send reset link
                  </Button>
                </form>
              )}

              <Box sx={{ mt: 2.5, textAlign: "center" }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", color: GOLD, fontWeight: 600,
                    "&:hover": { bgcolor: "rgba(201,168,76,0.08)" } }}
                >
                  Back to login
                </Button>
              </Box>
            </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
